import {
  localAuth,
  noopAnalytics,
  noopCrash,
  staticRemoteConfig,
  noopPush,
  newRequestId,
  type AccountCommand,
  type AppServices,
  type KeyValueStore,
} from '@mendikot/app-core';
import type { CommandPayload, Reply } from '@mendikot/protocol';
import { getFirebase, loadOnline, useEmulators } from './firebase';
import { LazyAuthService } from './lazyAuth';
import { LazyFirebaseGateway } from './lazyGateway';
import { SentryCrashReporter, WebAnalytics, WebPerf } from './observability';
import { WebPush } from './push';
import { WebRemoteConfig } from './remoteConfig';

export const APP_VERSION = __APP_VERSION__;

export function browserStore(): KeyValueStore {
  return {
    get: (key) => {
      try {
        return localStorage.getItem(key);
      } catch {
        return null;
      }
    },
    set: (key, value) => {
      try {
        localStorage.setItem(key, value);
      } catch {
        // Storage may be blocked (private mode) — ignore.
      }
    },
    remove: (key) => {
      try {
        localStorage.removeItem(key);
      } catch {
        // ignore
      }
    },
  };
}

export interface WebServices extends AppServices {
  /** Account-level callables (merge guest, delete account, topics). */
  account<T extends AccountCommand>(type: T, payload: CommandPayload<T>): Promise<Reply>;
  /** Applies the analytics/crash consent decision to every SDK. */
  applyConsent(granted: boolean): void;
  remote: WebRemoteConfig;
}

export function createWebServices(kv: KeyValueStore, lang: () => string): WebServices {
  const fb = getFirebase();
  const crash = fb ? new SentryCrashReporter() : null;
  const crashReporter = crash ?? noopCrash;

  const account = async <T extends AccountCommand>(
    type: T,
    payload: CommandPayload<T>,
  ): Promise<Reply> => {
    if (!fb) return { ok: false, code: 'INTERNAL', message: 'offline' };
    try {
      const [{ functions }, { apiCallable }] = await Promise.all([loadOnline(), import('./api')]);
      const result = await apiCallable<unknown, Reply>(functions)({
        type,
        payload,
        meta: { requestId: newRequestId(), appVersion: APP_VERSION, platform: 'web' },
      });
      return result.data;
    } catch (error) {
      crashReporter.captureError(error, { command: type });
      return { ok: false, code: 'INTERNAL' };
    }
  };

  // Analytics, Performance and Remote Config have no emulators; in emulator mode they stay off.
  const cloud = fb && !useEmulators ? fb : null;
  const analytics = cloud ? new WebAnalytics(cloud.app) : null;
  const perf = new WebPerf(cloud?.app ?? null);
  const remote = new WebRemoteConfig(cloud?.app ?? null, crashReporter);
  const push = fb
    ? new WebPush(fb.app, (token, topics) => account('push.syncTopics', { token, topics }), lang)
    : null;
  const auth = fb
    ? new LazyAuthService((guestIdToken) => account('profile.mergeGuest', { guestIdToken }))
    : localAuth(kv);

  return {
    platform: 'web',
    appVersion: APP_VERSION,
    auth,
    online: fb ? new LazyFirebaseGateway() : null,
    analytics: analytics ?? noopAnalytics,
    perf,
    crash: crashReporter,
    remoteConfig: cloud ? remote : staticRemoteConfig,
    remote,
    push: push ?? noopPush,
    kv,
    account,
    applyConsent(granted) {
      analytics?.setConsent(granted);
      void perf.enable(granted);
      if (granted) crash?.start(APP_VERSION);
      else crash?.stop();
    },
  };
}
